import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDashboardData } from "../context/DashboardDataContext";
import { SubmissionDetail } from "../components/SubmissionDetail";
import { Card } from "../components/ui/Card";
import { PageHeader } from "../components/ui/PageHeader";
import { PATTERN_LABELS } from "../dashboard/analytics";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function rowKey(row, index) {
  return row.id != null ? String(row.id) : `${row.email}-${row.createdAt}-${index}`;
}

export function SubmissionsExplorer() {
  const { submissions } = useDashboardData();
  const [selectedKey, setSelectedKey] = useState(null);

  const selected = useMemo(() => {
    if (selectedKey == null) return null;
    return submissions.find((row, i) => rowKey(row, i) === selectedKey) ?? null;
  }, [submissions, selectedKey]);

  const closeModal = () => setSelectedKey(null);

  useEffect(() => {
    if (selectedKey == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedKey]);

  useEffect(() => {
    if (selectedKey == null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selectedKey]);

  const modal =
    selected &&
    createPortal(
      <div
        className="submission-modal-backdrop"
        role="presentation"
        onClick={closeModal}
      >
        <div
          className="submission-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="submission-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="submission-modal__toolbar">
            <div>
              <h2 id="submission-modal-title" className="submission-modal__title">
                Full submission
              </h2>
              <p className="submission-modal__subtitle">
                {selected.full_name || selected.name || "—"} · {selected.email || "—"} ·{" "}
                {formatDate(selected.createdAt)}
              </p>
            </div>
            <button
              type="button"
              className="submission-modal__close"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <div className="submission-modal__scroll">
            <SubmissionDetail row={selected} />
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="page page--submissions">
      <PageHeader
        eyebrow="Data explorer"
        title="Submissions"
        description="Every row from the API for the active filters. Click View to open the full questionnaire and results in a popup."
      />

      <Card
        title={`${submissions.length} records`}
        subtitle="Click View on any row to see all answers and scored results."
      >
        <div className="table-wrap table-wrap--rounded">
          <table className="data-table data-table--dense">
            <thead>
              <tr>
                <th aria-label="Actions" />
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Created</th>
                <th>Primary</th>
                <th>Overall %</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((row, index) => {
                const key = rowKey(row, index);
                return (
                  <tr key={key} className="data-table__row">
                    <td>
                      <button
                        type="button"
                        className="btn-table-select"
                        onClick={() => setSelectedKey(key)}
                      >
                        View
                      </button>
                    </td>
                    <td>{row.id ?? "—"}</td>
                    <td>{row.full_name || row.name || "—"}</td>
                    <td className="cell-clip">{row.email || "—"}</td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>{PATTERN_LABELS[row.primary] || row.primary || "—"}</td>
                    <td>{row.overallPercent ?? "—"}</td>
                    <td>{row.revenue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {modal}
    </div>
  );
}
